// ABOUTME: One agent run's activity, between the message that woke it and its reply: a greyed,
// collapsed row ("Working for 12 s · 3 steps" with a ticking clock, then "Worked for 42 s ·
// 6 steps") that expands to the run in order: the agent's thinking between calls, and each tool
// call (its title and short subject, how long it took, a pulsing dot while it runs, "(it
// failed)" when it did) that opens to its input and result. A hand-off step links to its
// brief's card. Read live from the run's stored steps (REST /agent-runs, refreshed by the
// event stream), so it survives a reload.
import { useEffect, useId, useState } from 'react'
import { Check, ChevronRight, CircleAlert, Loader2 } from 'lucide-react'
import type { AgentRun, AgentRunStep } from '@/api/agentRuns'
import { cn } from '@/lib/utils'

function seconds(run: AgentRun, now: number): number {
  const end = run.completed_at ? Date.parse(run.completed_at) : now
  return Math.max(1, Math.round((end - Date.parse(run.started_at)) / 1000))
}

/** Re-renders every second while `live`, so the header's clock ticks. */
function useNow(live: boolean): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!live) return
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [live])
  return now
}

function stepSeconds(step: AgentRunStep): number | null {
  if (!step.started_at || !step.ended_at) return null
  const total = Math.round((Date.parse(step.ended_at) - Date.parse(step.started_at)) / 1000)
  return total >= 1 ? total : null
}

function duration(total: number): string {
  if (total < 60) return `${total} s`
  const minutes = Math.floor(total / 60)
  const rest = total % 60
  return rest ? `${minutes} min ${rest} s` : `${minutes} min`
}

const isThinking = (step: AgentRunStep) => step.kind === 'thinking'

function runSummary(run: AgentRun, now: number): string {
  const count = run.steps.filter((step) => !isThinking(step)).length
  const steps = count ? ` · ${count} ${count === 1 ? 'step' : 'steps'}` : ''
  const time = duration(seconds(run, now))
  return run.status === 'running' ? `Working for ${time}${steps}` : `Worked for ${time}${steps}`
}

function StepIcon({ status }: { status: AgentRunStep['status'] }) {
  if (status === 'done') return <Check className="size-3.5 shrink-0" aria-label="done" />
  if (status === 'failed')
    return <CircleAlert className="text-destructive size-3.5 shrink-0" aria-label="failed" />
  return (
    <span className="flex size-3.5 shrink-0 items-center justify-center" aria-label="running">
      <span className="bg-primary size-1.5 rounded-full motion-safe:animate-pulse" />
    </span>
  )
}

function StepDetail({ name, value }: { name: string; value: string | null }) {
  if (!value) return null
  return (
    <div>
      <p className="text-[0.6875rem] font-medium tracking-wide uppercase">{name}</p>
      <pre className="bg-muted/60 text-foreground/80 mt-0.5 max-h-60 overflow-auto rounded-md p-2 font-mono text-[0.6875rem] break-words whitespace-pre-wrap">
        {value}
      </pre>
    </div>
  )
}

function ThinkingLine({ step }: { step: AgentRunStep }) {
  return (
    <li className="text-muted-foreground/90 italic" data-testid="run-thinking">
      <p className="break-words whitespace-pre-wrap">{step.text}</p>
    </li>
  )
}

function StepLine({ step }: { step: AgentRunStep }) {
  const [open, setOpen] = useState(false)
  const base = step.subject ? `${step.title}: ${step.subject}` : step.title
  const label = step.status === 'failed' ? `${base} (it failed)` : base
  const took = step.status === 'running' ? null : stepSeconds(step)
  const hasDetail = Boolean(step.input || step.output)
  return (
    <li data-testid="run-step" data-status={step.status}>
      <div className="flex items-start gap-2">
        <span className="mt-0.5">
          <StepIcon status={step.status} />
        </span>
        {step.brief_id ? (
          <a
            href={`#brief-${step.brief_id}`}
            className="break-words underline underline-offset-2"
            data-testid="run-step-brief-link"
            onClick={(event) => {
              const card = document.getElementById(`brief-${step.brief_id}`)
              if (!card) return
              event.preventDefault()
              card.scrollIntoView({ behavior: 'smooth', block: 'center' })
            }}
          >
            {label}
          </a>
        ) : hasDetail ? (
          <button
            type="button"
            className="hover:text-foreground text-left break-words"
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
            data-testid="run-step-toggle"
          >
            {label}
            <ChevronRight
              className={cn('ml-1 inline size-3 transition-transform', open && 'rotate-90')}
            />
          </button>
        ) : (
          <span className="break-words">{label}</span>
        )}
        {took ? <span className="shrink-0 tabular-nums">· {took} s</span> : null}
      </div>
      {open ? (
        <div className="mt-1.5 ml-5.5 space-y-1.5" data-testid="run-step-detail">
          <StepDetail name="Input" value={step.input} />
          <StepDetail name="Result" value={step.output} />
        </div>
      ) : null}
    </li>
  )
}

export function RunActivityRow({
  run,
  defaultOpen = false,
}: {
  run: AgentRun
  defaultOpen?: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)
  const listId = useId()
  const live = run.status === 'running'
  const now = useNow(live)
  const hasSteps = run.steps.length > 0
  if (!hasSteps && !live) return null
  return (
    <div className="text-muted-foreground text-xs" data-testid="run-activity" data-run-id={run.id}>
      <button
        type="button"
        className="hover:text-foreground flex min-h-11 items-center gap-1 rounded-md pr-2 transition-colors"
        aria-expanded={open}
        aria-controls={listId}
        disabled={!hasSteps}
        onClick={() => setOpen((value) => !value)}
        data-testid="run-activity-toggle"
      >
        {live ? (
          <Loader2 className="size-3.5 animate-spin" aria-label="working" />
        ) : (
          <ChevronRight className={cn('size-3.5 transition-transform', open && 'rotate-90')} />
        )}
        <span data-testid="run-activity-summary">{runSummary(run, now)}</span>
        {live && hasSteps ? (
          <ChevronRight className={cn('size-3.5 transition-transform', open && 'rotate-90')} />
        ) : null}
      </button>
      {open && hasSteps ? (
        <ol id={listId} className="ml-1.5 space-y-1.5 border-l pb-1 pl-3" data-testid="run-steps">
          {run.steps.map((step, index) =>
            isThinking(step) ? (
              <ThinkingLine key={`${index}-thinking`} step={step} />
            ) : (
              <StepLine key={`${index}-${step.title}`} step={step} />
            )
          )}
        </ol>
      ) : null}
    </div>
  )
}
